export const RISK_STANDARD_LEVELS = [
  {
    standard_id: 1,
    min: 1,
    max: 3,
    label: "현재 안전대책 유지",
    bg: "#dcfce7",
    border: "#86efac",
    fg: "#166534",
    solid: "#16a34a",
    muiColor: "success",
    requiresImprove: false,
  },
  {
    standard_id: 2,
    min: 4,
    max: 6,
    label: "안전정보 및 주기적 안전보건교육 제공",
    bg: "#ecfccb",
    border: "#bef264",
    fg: "#3f6212",
    solid: "#65a30d",
    muiColor: "success",
    requiresImprove: false,
  },
  {
    standard_id: 3,
    min: 7,
    max: 8,
    label: "정비·보수 전 안전보건대책 수립",
    bg: "#fef9c3",
    border: "#fde68a",
    fg: "#854d0e",
    solid: "#ca8a04",
    muiColor: "warning",
    requiresImprove: false,
  },
  {
    standard_id: 4,
    min: 9,
    max: 12,
    label: "즉시 개선",
    bg: "#ffedd5",
    border: "#fdba74",
    fg: "#9a3412",
    solid: "#ea580c",
    muiColor: "warning",
    requiresImprove: true,
  },
  {
    standard_id: 5,
    min: 13,
    max: 15,
    label: "긴급 임시안전보건대책 후 작업",
    bg: "#fee2e2",
    border: "#fca5a5",
    fg: "#991b1b",
    solid: "#dc2626",
    muiColor: "error",
    requiresImprove: true,
  },
  {
    standard_id: 6,
    min: 16,
    max: 20,
    label: "즉시 작업중지",
    bg: "#fecaca",
    border: "#ef4444",
    fg: "#7f1d1d",
    solid: "#b91c1c",
    muiColor: "error",
    requiresImprove: true,
  },
];

let currentStandards = [];
export const setCurrentRiskStandards = (standards) => { currentStandards = Array.isArray(standards) ? standards : []; };

export const RISK_STANDARD_NEUTRAL = {
  standard_id: null,
  min: null,
  max: null,
  label: "기준 선택 전",
  bg: "#f8fafc",
  border: "#e2e8f0",
  fg: "#475569",
  solid: "#64748b",
  muiColor: "default",
  requiresImprove: false,
  neutral: true,
};

const RISK_STANDARD_LABEL_ALIASES = {
  매우낮음: 1,
  낮음: 2,
  보통: 3,
  약간높음: 4,
  높음: 5,
  매우높음: 6,
  "현재 안전대책 유지": 1,
  "안전정보 및 주기적 안전보건교육 제공": 2,
  "정비·보수 전 안전보건대책 수립": 3,
  "즉시 개선": 4,
  "긴급 임시안전보건대책 후 작업": 5,
  "즉시 작업중지": 6,
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const normalizeLevel = (level = {}) => ({
  hex_color: level.hex_color,
  standard_id: toNumber(level.standard_id),
  min: toNumber(level.risk_level_min ?? level.min),
  max: toNumber(level.risk_level_max ?? level.max),
  label: level.manage_criteria || level.label,
  requiresImprove: Number(level.requires_improve) === 1 || level.requiresImprove === true,
});

const mergeLevel = (base, override = {}) => ({
  ...base,
  // Selecting the default swatch must retain the same complete palette as null.
  ...(/^#[0-9a-f]{6}$/i.test(override.hex_color || '') &&
    override.hex_color.toLowerCase() !== base.solid.toLowerCase() ? {
    solid: override.hex_color,
    bg: `${override.hex_color}20`,
    border: override.hex_color,
    fg: '#111827',
  } : {}),
  label: override.label || base.label,
  requiresImprove:
    override.requiresImprove === true || override.requiresImprove === false
      ? override.requiresImprove
      : base.requiresImprove,
});

export const getRiskStandardByScore = (score, standards = currentStandards) => {
  const riskScore = toNumber(score);
  if (!riskScore) return RISK_STANDARD_NEUTRAL;

  const dbLevel = standards.map(normalizeLevel).find(
    (item) => item.min && item.max && riskScore >= item.min && riskScore <= item.max
  );
  const baseLevel = RISK_STANDARD_LEVELS.find(
    (item) => riskScore >= item.min && riskScore <= item.max
  );

  if (!baseLevel && !dbLevel) return RISK_STANDARD_NEUTRAL;
  if (!baseLevel) return { ...RISK_STANDARD_NEUTRAL, ...dbLevel, neutral: false };
  return mergeLevel(baseLevel, dbLevel);
};

export const getRiskStandardById = (standardId, standards = currentStandards) => {
  const id = toNumber(standardId);
  if (!id) return RISK_STANDARD_NEUTRAL;

  const dbLevel = standards.map(normalizeLevel).find((item) => item.standard_id === id);
  const baseLevel = RISK_STANDARD_LEVELS.find((item) => item.standard_id === id);

  if (!baseLevel && !dbLevel) return RISK_STANDARD_NEUTRAL;
  if (!baseLevel) return { ...RISK_STANDARD_NEUTRAL, ...dbLevel, neutral: false };
  return mergeLevel(baseLevel, dbLevel);
};

export const getRiskStandardByLabel = (label, standards = currentStandards) => {
  if (!label) return RISK_STANDARD_NEUTRAL;
  const text = String(label).trim();
  const aliasId = RISK_STANDARD_LABEL_ALIASES[text];
  if (aliasId) return getRiskStandardById(aliasId, standards);

  const dbLevel = standards
    .map(normalizeLevel)
    .find((item) => item.label && String(item.label).trim() === text);
  if (!dbLevel) return RISK_STANDARD_NEUTRAL;
  return getRiskStandardById(dbLevel.standard_id, standards);
};

export const getRiskStandardStyle = (source, standards = currentStandards) => {
  if (source === undefined || source === null || source === "") return RISK_STANDARD_NEUTRAL;

  if (typeof source === "number") {
    return getRiskStandardByScore(source, standards);
  }

  if (typeof source === "string") {
    return Number.isFinite(Number(source))
      ? getRiskStandardByScore(source, standards)
      : getRiskStandardByLabel(source, standards);
  }

  const score =
    toNumber(source.risk_score) ||
    toNumber(source.score) ||
    toNumber(source.frequency_level) * toNumber(source.importancy_level);
  if (score) return getRiskStandardByScore(score, standards);

  if (source.standard_id || source.improve_after_risk) {
    return getRiskStandardById(source.standard_id || source.improve_after_risk, standards);
  }

  if (source.risk_level_min && source.risk_level_max) {
    return getRiskStandardByScore(source.risk_level_min, standards);
  }

  if (source.manage_criteria || source.label) {
    return getRiskStandardByLabel(source.manage_criteria || source.label, standards);
  }

  return RISK_STANDARD_NEUTRAL;
};

export const getRiskStandardChipSx = (source, standards = currentStandards) => {
  const style = getRiskStandardStyle(source, standards);
  const rgb = style.solid.slice(1).match(/.{2}/g).map(v => parseInt(v, 16));
  const textColor = rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114 > 150 ? '#111827' : '#fff';
  return {
    bgcolor: `${style.solid} !important`,
    color: `${textColor} !important`,
    borderColor: `${style.solid} !important`,
    fontWeight: 900,
    "& .MuiChip-label": {
      color: textColor,
    },
  };
};

export const getRiskStandardSoftChipSx = (source, standards = currentStandards) => {
  const style = getRiskStandardStyle(source, standards);
  return {
    bgcolor: `${style.bg} !important`,
    color: `${style.fg} !important`,
    borderColor: `${style.border} !important`,
    fontWeight: 900,
    "& .MuiChip-label": {
      color: style.fg,
    },
  };
};

export const getRiskStandardBoxSx = (source, standards = currentStandards) => {
  const style = getRiskStandardStyle(source, standards);
  return {
    bgcolor: style.bg,
    borderColor: style.border,
    color: style.fg,
  };
};

export const getRiskStandardMatrixStyle = (source, standards = currentStandards) => {
  const { solid } = getRiskStandardStyle(source, standards);
  const channels = solid.slice(1).match(/.{2}/g).map(value => parseInt(value, 16));
  // Use opaque tints so preview and saved colors do not depend on the surface behind them.
  const tint = strength => `#${channels.map(channel =>
    Math.round(channel * strength + 255 * (1 - strength)).toString(16).padStart(2, '0')
  ).join('')}`;
  return { background: tint(0.45), borderColor: tint(0.65), color: '#111827' };
};

export const RISK_STANDARD_BACKGROUND_BY_ID = RISK_STANDARD_LEVELS.reduce((acc, item) => {
  acc[item.standard_id] = item.bg;
  acc[item.label] = item.bg;
  return acc;
}, {});
